import { PolicyRegion } from '../types/policy-types';

export const getPolicyRegion = (
  entityCode: string,
  companyCode: string,
  policyNumber: string,
): PolicyRegion => {
  switch (entityCode) {
    case 'LHK':
      return 'HK';
    case 'LMA':
      return 'MA';
    case 'EHK':
      return companyCode === '5' ? 'MA' : 'HK';
    case 'GHK':
      // P&C
      return 'Both';
    case 'G4HK':
      // FC
      return 'Both';
    case 'UHK':
    case 'HHK': {
      if (policyNumber.startsWith('888')) {
        return 'HK';
      } else if (policyNumber.startsWith('828')) {
        return 'MA';
      }
      return 'Both';
    }
    default:
      console.warn(
        `Could not detect policy region for entityCode: ${entityCode}`,
      );
      return 'Both';
  }
};

export const getPoliciesRegion = (
  policies: {
    entityCode: string;
    companyCode: string;
    policyNumber: string;
  }[],
): PolicyRegion => {
  const result = new Set<PolicyRegion>();
  for (const policy of policies) {
    result.add(
      getPolicyRegion(
        policy.entityCode,
        policy.companyCode,
        policy.policyNumber,
      ),
    );
  }
  if (result.has('Both') || (result.has('HK') && result.has('MA'))) {
    return 'Both';
  } else if (result.has('MA')) {
    return 'MA';
  } else {
    return 'HK';
  }
};
// Entity code regexes that determine which product line owns a policy.
const ENTITY_CODE_LIFE = /^(LMA|LHK|UHK|HHK)/;
const ENTITY_CODE_GI = /^GHK/;
const ENTITY_CODE_FC = /^G4HK/;
const ENTITY_CODE_EB = /^EHK/;

export enum PolicyType {
  LIFE_SAVINGS = 'LIFE_SAVINGS', // Life Insurance & Savings
  LIFE_INVESTMENT_LINKED = 'LIFE_INVESTMENT_LINKED', // Investment Linked Insurance
  INDIVIDUAL_HEALTH = 'INDIVIDUAL_HEALTH', // Individual Health & Protection (Life GROUP_HEALTH / FC)
  HOME_INSURANCE = 'HOME_INSURANCE', // Household Protection / Fire Insurance
  TRAVEL_INSURANCE = 'TRAVEL_INSURANCE', // Travel Insurance
  MOTOR_INSURANCE = 'MOTOR_INSURANCE', // Motor Insurance
  HELPER_INSURANCE = 'HELPER_INSURANCE', // Helper Insurance
  PERSONAL_ACCIDENT = 'PERSONAL_ACCIDENT', // Personal Accident Insurance
  EMPLOYEE_BENEFITS = 'EMPLOYEE_BENEFITS', // Employee Benefits
  UNKNOWN = 'UNKNOWN',
}

// Life policyCategory → PolicyType
const LIFE_CATEGORY_MAP: Record<string, PolicyType> = {
  SAVINGS_PRODUCT: PolicyType.LIFE_SAVINGS,
  UNIT_LINK_PRODUCT: PolicyType.LIFE_INVESTMENT_LINKED,
  GROUP_HEALTH_PRODUCT: PolicyType.INDIVIDUAL_HEALTH,
};

// GI policyCategory → PolicyType
const GI_CATEGORY_MAP: Record<string, PolicyType> = {
  'HOME INSURANCE': PolicyType.HOME_INSURANCE,
  'TRAVEL INSURANCE': PolicyType.TRAVEL_INSURANCE,
  'CAR INSURANCE': PolicyType.MOTOR_INSURANCE,
  'HELPER INSURANCE': PolicyType.HELPER_INSURANCE,
  'PERSONAL ACCIDENT INSURANCE': PolicyType.PERSONAL_ACCIDENT,
};

/**
 * Determines the display policy type for a policy object.
 *
 * Decision criteria (in order):
 * 1. entityCode regex decides the product line (Life / GI / FC / EB).
 * 2. For Life, `policyCategory` is matched against LIFE_CATEGORY_MAP.
 *    Policies without a recognised policyCategory fall back to INDIVIDUAL_HEALTH
 * 3. For GI, `policyCategory` is matched against GI_CATEGORY_MAP.
 *    Note: "Fire Insurance" policies carry policyCategory === 'HOME INSURANCE',
 *    so they resolve to HOME_INSURANCE via the same mapping.
 * 4. FC (entityCode ^G4HK) → always INDIVIDUAL_HEALTH.
 * 5. EB (entityCode ^EHK) → always EMPLOYEE_BENEFITS.
 *
 * @param {Object} policy - A flattened policy object (fields from `policy.*` merged to root).
 * @returns {string} One of the POLICY_TYPE constants.
 */
export const getPolicyType = (policy: {
  entityCode: string;
  policyCategory: string;
}): PolicyType => {
  const { entityCode, policyCategory } = policy || {};

  if (!entityCode) {
    return PolicyType.UNKNOWN;
  }

  if (ENTITY_CODE_EB.test(entityCode)) {
    return PolicyType.EMPLOYEE_BENEFITS;
  }

  if (ENTITY_CODE_FC.test(entityCode)) {
    return PolicyType.INDIVIDUAL_HEALTH;
  }

  if (ENTITY_CODE_LIFE.test(entityCode)) {
    return LIFE_CATEGORY_MAP[policyCategory] ?? PolicyType.INDIVIDUAL_HEALTH;
  }

  if (ENTITY_CODE_GI.test(entityCode)) {
    return GI_CATEGORY_MAP[policyCategory] ?? PolicyType.UNKNOWN;
  }

  return PolicyType.UNKNOWN;
};
